/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes */
import Fragment from 'ember-data-model-fragments/fragment';
import { computed } from '@ember/object';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type DataType from '@linc-technologies/linced-common/enums/data-type';
import type FieldOption from '@linc-technologies/linced-common/models/people-schema/field-option';
import { getValueKey } from '@linc-technologies/linced-common/utils/dynamic-field-types';

// The value of each DataType: valueString, valueList, valueInt or valueFloat,
// valueBool, valueValueList. null when the datatype is not set.
export type DynamicValue =
  | string
  | StatefulArray<string>
  | number
  | boolean
  | FragmentArray<FieldOption>
  | null;

// Proto: the superset of the FieldValue messages (people_schema.FieldValue
// and the entity FieldValue messages, which add key, valueValueList,
// requiredRole and isReadOnly).
// Entities get zero values from transmogrifyFields in the DynamicFields
// serializer. The defaults are for fragments that bypass it, such as schema
// `default` values.
export default class Value extends Fragment {
  declare [Type]: 'dynamic-field/value';

  @attr('string')
  declare key: string | undefined;

  @attr('number', { defaultValue: 0 })
  declare datatype: EnumValue<typeof DataType> | 0;

  @computed(
    'datatype',
    'valueString',
    'valueList',
    'valueList.[]',
    'valueInt',
    'valueFloat',
    'valueBool',
    'valueValueList',
    'valueValueList.[]',
    {
      get(this: Value): DynamicValue {
        const key = getValueKey(this.datatype);
        if (key.length) {
          return this.get(key) as DynamicValue;
        }

        return null;
      },
      set(this: Value, _: string, value: DynamicValue): DynamicValue {
        const key = getValueKey(this.datatype);
        if (key.length) {
          this.set(key, value);
          return value;
        }
        return null;
      },
    },
  )
  declare value: DynamicValue;

  @attr('string', { defaultValue: '' })
  declare valueString: string;

  @array('string', { defaultValue: () => [] })
  declare valueList: StatefulArray<string>;

  @attr('number', { defaultValue: 0 })
  declare valueInt: number;

  @attr('number', { defaultValue: 0 })
  declare valueFloat: number;

  @attr('boolean', { defaultValue: false })
  declare valueBool: boolean;

  @fragmentArray('people-schema/field-option', { defaultValue: () => [] })
  declare valueValueList: FragmentArray<FieldOption>;

  // requiredRole specifies the Field Access role required to view this field,
  // if applicable.  If empty, anyone with access to the entity can see it.
  @attr('string', { defaultValue: '' })
  declare requiredRole: string;

  // isReadOnly specifies whether this field is read-only.
  @attr('boolean', { defaultValue: false })
  declare isReadOnly: boolean;
}
